import { makeStyles } from "@material-ui/core/styles";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableFooter from "@material-ui/core/TableFooter";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import { CSSProperties } from "@material-ui/styles";
import ResponsiveTable from "@saleor/components/ResponsiveTable";
import Skeleton from "@saleor/components/Skeleton";
import TableCellHeader from "@saleor/components/TableCellHeader";
import TablePagination from "@saleor/components/TablePagination";
import { maybe, renderCollection } from "@saleor/misc";
import { ListProps } from "@saleor/types";
import React from "react";
import { FormattedMessage } from "react-intl";
import Moment from "react-moment";

import { storeOrdersFinance_storeOrdersFinance_edges_node } from "../../types/storeOrdersFinance";

const useStyles = makeStyles(
  theme => {
    const overflowing: CSSProperties = {
      overflow: "hidden",
      textOverflow: "ellipsis"
    };
    return {
      colMedium: {
        overflowing,
        width: "12em",
        [theme.breakpoints.down("md")]: {
          width: "auto"
        }
      },
      colNarrow: {
        overflowing,
        width: "8em",
        [theme.breakpoints.down("md")]: {
          width: "auto"
        }
      },
      colRow: {
        "& th": {
          fontWeight: 600 as 600,
          paddingBottom: theme.spacing(1),
          paddingTop: theme.spacing(1)
        }
      },
      colWide: {
        overflowing,
        width: "16em",
        [theme.breakpoints.down("md")]: {
          width: "auto"
        }
      },
      table: {
        tableLayout: "fixed"
      },
      tableRow: {
        cursor: "pointer"
      }
    };
  },
  { name: "storeOrdersFinanceList" }
);

interface StoreOrdersFinanceListProp extends ListProps {
  storeOrdersFinance: storeOrdersFinance_storeOrdersFinance_edges_node[];
}

const StoreOrdersFinanceList: React.FC<StoreOrdersFinanceListProp> = ({
  storeOrdersFinance,
  onNextPage,
  onPreviousPage,
  onUpdateListSettings,
  onRowClick,
  pageInfo,
  settings,
  disabled
}) => {
  const colNum = 6;
  const classes = useStyles({});

  return (
    <>
      <ResponsiveTable className={classes.table}>
        <TableHead>
          <TableRow>
            <TableCellHeader className={classes.colWide}>
              <FormattedMessage defaultMessage="Mall" />
            </TableCellHeader>
            <TableCellHeader className={classes.colWide}>
              <FormattedMessage defaultMessage="Brand" />
            </TableCellHeader>
            <TableCellHeader className={classes.colMedium}>
              <FormattedMessage defaultMessage="Order Status" />
            </TableCellHeader>

            <TableCellHeader className={classes.colWide}>
              <FormattedMessage defaultMessage="Order Created Date" />
            </TableCellHeader>
            <TableCellHeader className={classes.colMedium}>
              <FormattedMessage defaultMessage="Order Code" />
            </TableCellHeader>
            <TableCellHeader className={classes.colMedium}>
              <FormattedMessage defaultMessage="Store Order Code" />
            </TableCellHeader>
          </TableRow>
        </TableHead>
        <TableFooter>
          <TableRow>
            <TablePagination
              colSpan={colNum}
              settings={settings}
              hasNextPage={
                pageInfo && !disabled ? pageInfo.hasNextPage : undefined
              }
              onNextPage={onNextPage}
              onUpdateListSettings={onUpdateListSettings}
              hasPreviousPage={
                pageInfo && !disabled ? pageInfo.hasPreviousPage : undefined
              }
              onPreviousPage={onPreviousPage}
            />
          </TableRow>
        </TableFooter>
        <TableBody>
          {storeOrdersFinance &&
            renderCollection(
              storeOrdersFinance,
              (storeOrder, index) => (
                <TableRow
                  className={!!storeOrder ? classes.tableRow : undefined}
                  hover={!!storeOrder}
                  key={storeOrder ? index : "skeleton"}
                  onClick={
                    storeOrder
                      ? onRowClick(btoa(`StoreOrder:${atob(storeOrder.id)}`))
                      : undefined
                  }
                >
                  <TableCell>
                    {maybe<React.ReactNode>(
                      () => storeOrder.mall_name,
                      <Skeleton />
                    )}
                  </TableCell>

                  <TableCell>
                    {maybe<React.ReactNode>(
                      () => storeOrder.brand_name,
                      <Skeleton />
                    )}
                  </TableCell>

                  <TableCell>
                    {maybe<React.ReactNode>(
                      () => storeOrder.order_status.toUpperCase(),
                      <Skeleton />
                    )}
                  </TableCell>

                  <TableCell>
                    <Moment format="YYYY-MM-DD HH:mm:ss" tz="Asia/Manila">
                      {storeOrder?.order_date ? (
                        storeOrder.order_date
                      ) : (
                        <Skeleton />
                      )}
                    </Moment>
                  </TableCell>
                  <TableCell>
                    {maybe<React.ReactNode>(
                      () => storeOrder.order_code,
                      <Skeleton />
                    )}
                  </TableCell>
                  <TableCell>
                    {maybe<React.ReactNode>(
                      () => storeOrder.store_order_code,
                      <Skeleton />
                    )}
                  </TableCell>
                </TableRow>
              ),
              () => (
                <TableRow>
                  <TableCell colSpan={colNum}>
                    <FormattedMessage defaultMessage="No store orders found" />
                  </TableCell>
                </TableRow>
              )
            )}
        </TableBody>
      </ResponsiveTable>
    </>
  );
};

export default StoreOrdersFinanceList;
